import { CSSTransition } from 'react-transition-group';
import React from 'react';

import {
    IconClose,
    IconHamburger
} from '$partials/icon/index.js';

import { useDialog } from '$hooks/dialog.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Header}
 */
const Header = ({
    footerLinks1 = [],
    footerLinks3 = [],
    footerLinks4 = [],
    isParentHomePage = false,
    logoAllPages = null,
    navigation = ''
}) => {
    // misc: dialog
    const dialogAnimationDuration = 700;

    // misc: padding classes
    let classes = '';
    if (isParentHomePage) {
        classes = 'pb-12 pt-10 md:pb-20 md:pt-37';
    } else {
        classes = 'pb-12 pt-10 md:py-24';
    }

    const {
        dialogRef: navigationDialogRef,
        handleClose: navigationHandleClose,
        handleOpen: navigationHandleOpen,
        isOpen: navigationIsOpen,
        scrollRef: navigationScrollRef,
        transitionRef: navigationTransitionRef
    } = useDialog({ exitDelay: dialogAnimationDuration });

    return (
        <header className={isParentHomePage ? 'sticky top-0 z-10 md:static' : 'sticky top-0 md:h-70 z-10'}>
            <div className={`${isParentHomePage ? 'home-page' : ''} layout-container h-full bg-neutral-00`}>
                <div className={`relative w-full flex flex-wrap items-center bg-neutral-00 z-10 ${classes}`}>
                    <div className='w-6/12'>
                        {/* H1 for Home Page */}
                        {isParentHomePage && (
                            <h1 className='sr-only'>Colour Works</h1>
                        )}

                        {/* Logo: word mark */}
                        {isParentHomePage ? (
                            <img
                                alt={logoAllPages?.alt}
                                className='w-5/6 md:w-3/6 pointer-events-none select-none'
                                height={logoAllPages?.height}
                                loading='eager'
                                src={logoAllPages?.original}
                                width={logoAllPages?.width} />
                        ) : (
                            <a
                                aria-label='Home Page'
                                className='block md:w-1/6'
                                href='/'>
                                <img
                                    alt={logoAllPages?.alt}
                                    className='pointer-events-none select-none'
                                    height={logoAllPages?.height}
                                    loading='eager'
                                    src={logoAllPages?.original}
                                    width={logoAllPages?.width} />
                            </a>
                        )}
                    </div>

                    <div className='w-6/12 flex items-center justify-between'>
                        {navigation && (
                            <>
                                {/* Primary navigation */}
                                <nav
                                    className='hidden md:flex type-style-body flex-wrap items-center w-full gap-x-12 gap-y-9 md:flex-nowrap md:justify-between md:gap-0 [&_p]:contents hover:[&_a]:underline hover:[&_a]:underline-offset-2'
                                    dangerouslySetInnerHTML={{ __html: navigation }}
                                    id='primary-navigation' />

                                {/* Mobile navigation open button */}
                                <button
                                    aria-label='Open primary navigation'
                                    className='ml-auto md:hidden'
                                    id='primary-navigation'
                                    onClick={() => navigationHandleOpen()}
                                    type='button'>
                                    <IconHamburger />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Bottom border */}
                    <div className={`absolute bottom-0 h-1 w-full bg-neutral-400${isParentHomePage ? ' sm:bg-transparent' : ''}`} />
                </div>
            </div>

            {/* Primary navigtion: mobile */}
            <dialog
                className={`dialog${navigationIsOpen ? ' dialog--open' : ''}`}
                onClick={(e) => (e?.target === navigationDialogRef?.current) && navigationHandleClose()}
                ref={navigationDialogRef}>
                <div className='dialog__backdrop'>
                    <CSSTransition
                        classNames='slide-in-left'
                        in={navigationIsOpen}
                        nodeRef={navigationTransitionRef}
                        timeout={dialogAnimationDuration}>
                        <div
                            className='container--no-margin h-full w-full'
                            ref={navigationTransitionRef}
                            style={{ transitionDuration: dialogAnimationDuration + 'ms' }}>
                            <div
                                className='h-full w-full overflow-y-auto bg-neutral-00'
                                ref={navigationScrollRef}>
                                <div className='relative h-full px-8 flex flex-col pointer-events-auto'>
                                    <div className='pb-12 pt-10 flex justify-between'>
                                        {/* Logo: word mark */}
                                        {isParentHomePage ? (
                                            <img
                                                alt={logoAllPages?.alt || ''}
                                                className='w-4/12 pointer-events-none select-none'
                                                height={logoAllPages?.height}
                                                loading='eager'
                                                role='presentation'
                                                src={logoAllPages?.original}
                                                width={logoAllPages?.width} />
                                        ) : (
                                            <a
                                                aria-label='Home Page'
                                                className='block sm:w-4/12'
                                                href='/'>
                                                <img
                                                    alt={logoAllPages?.alt || ''}
                                                    className='pointer-events-none select-none'
                                                    height={logoAllPages?.height}
                                                    loading='eager'
                                                    role='presentation'
                                                    src={logoAllPages?.original}
                                                    width={logoAllPages?.width} />
                                            </a>
                                        )}

                                        {/* Mobile navigation close button */}
                                        <button
                                            aria-label='Close primary navigaton'
                                            className='-mr-8'
                                            id='primary-navigation-close'
                                            onClick={() => navigationHandleClose()}
                                            type='button'>
                                            <IconClose />
                                        </button>
                                    </div>

                                    {/* Bottom border */}
                                    <div className='h-1 w-full bg-neutral-400' />

                                    <div className='h-full flex flex-col gap-y-60 py-20'>
                                        {/* Primary navigation */}
                                        <nav
                                            className='flex flex-col type-style-heading [&_p]:py-10 [&_p]:border-b [&_p:last-child]:border-none [&_p]:border-neutral-400'
                                            dangerouslySetInnerHTML={{ __html: navigation }} />

                                        {/* Footer links 3 */}
                                        <nav
                                            className='flex flex-col type-style-body'
                                            dangerouslySetInnerHTML={{ __html: footerLinks3 }} />

                                        <div className='mt-auto'>
                                            <div className='layout-grid'>
                                                {/* Footer links 1 */}
                                                <nav
                                                    className='layout-grid-col w-6/12 flex flex-col type-style-body'
                                                    dangerouslySetInnerHTML={{ __html: footerLinks1 }} />

                                                {/* Footer links 4 */}
                                                <nav
                                                    className='layout-grid-col w-6/12 flex flex-col type-style-body justify-end'
                                                    dangerouslySetInnerHTML={{ __html: footerLinks4 }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CSSTransition>
                </div>
            </dialog>
        </header>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Header;
